import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import InertiaPlugin from 'gsap/InertiaPlugin';
import LayoutUtil from '@frontend/utils/LayoutUtil';
import { getRelativeBoundsTo } from '@frontend/utils/PositioningUtil';
import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';

interface RequiredBoundsMinMax extends Draggable.BoundsMinMax {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
}

gsap.registerPlugin(Draggable, InertiaPlugin, MotionPathPlugin);

export default class History {
	private readonly element: HTMLElement;

	private readonly dots: HTMLElement[];

	public static domSelectors = {
		header: '.m-header',
		entry: '.m-history__entry',
		timeline: '.m-history__timeline',
		track: '.m-history__track',
		dot: '.m-history__entry-dot',
		knob: '.m-history__knob',
		knobLabel: '.m-history__knob-label',
		dotHitTarget: '.m-history__knob-dot-hit-target',
		firstYear: '.m-history__first-year',
		lastYear: '.m-history__last-year',
		waveWrapper: '.m-history__wave',
		wavePath: '.m-history__wave path',
		mobileSelectMenu: '.m-history__select-menu select',
		mobileLeftPagination: '.m-history__left-pagination',
		mobileRightPagination: '.m-history__right-pagination',
		mobilePaginationText: '.m-history__pagination-text'
	};

	public static stateClasses = {
		canScrollRight: 'can-scroll-right',
		canScrollLeft: 'can-scroll-left',
		isInDragState: 'is-in-drag-state'
	};

	public static attributes = {
		entryId: 'data-entry-id',
		entryYear: 'data-entry-year'
	};

	protected dotSpacing = 40;

	protected scrollAnimation?: number;

	protected scrollValue = 0;

	protected scrollSpeed = 10;

	protected knobDraggable!: GSAPDraggableVars;

	protected hitDetectionInterval?: number;

	protected currentId!: string;

	public constructor(element: HTMLElement) {
		this.element = element;
		this.dots = Array.from(this.element.querySelectorAll(History.domSelectors.dot));
		this.init();
	}

	private init(): void {
		this.initKnobDraggable();
		this.updateWave();
		this.updateDots();
		this.updateKnobY();
		this.updateScrollEdgeIndicators();
		this.setInitState();
		this.initMobileNavigation();

		window.addEventListener('resize', (): void => {
			this.adjustTimelinePositions();
			this.updateWave();
			this.updateDots();
			this.updateKnobY();
			this.updateDraggableBounds();
			this.updateScrollEdgeIndicators();
			this.moveKnobToDot(this.currentId);
		});

		this.dots.forEach((dot): void => {
			dot.addEventListener('click', (): void => {
				const id = dot.getAttribute(History.attributes.entryId);
				if (id) this.setActiveContent(id);
				this.dots.forEach((adot): void => {
					adot.removeAttribute('aria-selected');
				});
				dot.setAttribute('aria-selected', 'true');
			});
			dot.setAttribute('role', 'tab');
		});

		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler.bind(this));
	}

	protected initKnobDraggable(): void {
		const self = this;

		this.knobDraggable = Draggable.create(History.domSelectors.knob, {
			type: 'x',
			bounds: this.getDraggableBounds(),
			onPress(): void {
				self.showKnobLabel();
				self.startHitDetection();
				self.handleDragScroll(this as Draggable);
				self.element.classList.add(History.stateClasses.isInDragState);
			},
			onRelease(): void {
				self.hideKnobLabel();
				self.stopHitDetection();
				self.stopDotScroll();
				self.setActiveContent(self.currentId);
				self.element.classList.remove(History.stateClasses.isInDragState);
			},
			onDrag(): void {
				self.updateKnobY();
				self.handleDragScroll(this as Draggable);
			}
		});
	}

	protected setInitState(): void {
		const dots = Array.from(this.element.querySelectorAll(History.domSelectors.dot));
		const firstDotId = dots[0].getAttribute(History.attributes.entryId);
		const lastDotId = dots[dots.length - 1].getAttribute(History.attributes.entryId);

		let initialId = firstDotId;

		if (this.isRtl() && lastDotId) {
			initialId = lastDotId;
		}

		if (initialId) {
			this.setActiveContent(initialId, { alignWithViewport: false });
		}
	}

	protected initMobileNavigation(): void {
		const leftPagination = this.element.querySelector(History.domSelectors.mobileLeftPagination) as HTMLElement;
		const rightPagination = this.element.querySelector(History.domSelectors.mobileRightPagination) as HTMLElement;
		const selectMenu = this.element.querySelector(History.domSelectors.mobileSelectMenu) as HTMLSelectElement;

		leftPagination.addEventListener('click', (): void => {
			const id = leftPagination.getAttribute(History.attributes.entryId);
			if (id) this.setActiveContent(id);
		});

		rightPagination.addEventListener('click', (): void => {
			const id = rightPagination.getAttribute(History.attributes.entryId);
			if (id) this.setActiveContent(id);
		});

		selectMenu.addEventListener('change', (): void => {
			this.setActiveContent(selectMenu.options[selectMenu.selectedIndex].value);
		});
	}

	protected updateMobileNavigation(): void {
		const leftPagination = this.element.querySelector(History.domSelectors.mobileLeftPagination) as HTMLElement;
		const rightPagination = this.element.querySelector(History.domSelectors.mobileRightPagination) as HTMLElement;
		const selectMenu = this.element.querySelector(History.domSelectors.mobileSelectMenu) as HTMLSelectElement;
		const currentIndex = this.getDotIndexWithId(this.currentId);
		const dotData = this.getDotData();

		if (currentIndex === null) return;

		const nextYear = dotData[currentIndex + 1];
		const previousYear = dotData[currentIndex - 1];

		const updatePaginationItem = (element: HTMLElement, id: string, year: string): void => {
			const textElement = element.querySelector(History.domSelectors.mobilePaginationText) as HTMLElement;
			element.classList.remove('is-hidden');
			element.setAttribute(History.attributes.entryId, id);
			textElement.innerText = year;
		};

		const clearPaginationItem = (element: HTMLElement): void => {
			const textElement = element.querySelector(History.domSelectors.mobilePaginationText) as HTMLElement;
			element.classList.add('is-hidden');
			element.removeAttribute(History.attributes.entryId);
			textElement.innerText = '';
		};

		if (!nextYear) {
			clearPaginationItem(rightPagination);
		} else {
			updatePaginationItem(rightPagination, nextYear.id, nextYear.year);
		}

		if (!previousYear) {
			clearPaginationItem(leftPagination);
		} else {
			updatePaginationItem(leftPagination, previousYear.id, previousYear.year);
		}

		selectMenu.value = this.currentId;
	}

	protected scrollDotsLeft(): void {
		if (this.getScrolledAmount() + this.scrollSpeed > this.getScrollableAmount()) {
			this.scrollValue = this.getScrollableAmount() * -1;
		} else {
			this.scrollValue = this.scrollValue - this.scrollSpeed;
		}
		this.updateDots();
		this.updateScrollEdgeIndicators();
		this.scrollAnimation = requestAnimationFrame((): void => this.scrollDotsLeft());
	}

	protected scrollDotsRight(): void {
		if (this.getScrolledAmount() - this.scrollSpeed < 0) {
			this.scrollValue = 0;
		} else {
			this.scrollValue = this.scrollValue + this.scrollSpeed;
		}
		this.updateDots();
		this.updateScrollEdgeIndicators();
		this.scrollAnimation = requestAnimationFrame((): void => this.scrollDotsRight());
	}

	protected handleDragScroll(draggable: Draggable): void {
		if (draggable.x === draggable.maxX && !this.scrollAnimation) {
			this.scrollAnimation = requestAnimationFrame((): void => this.scrollDotsLeft());
		} else if (draggable.x === (draggable as any).minX && !this.scrollAnimation) {
			this.scrollAnimation = requestAnimationFrame((): void => this.scrollDotsRight());
		} else {
			this.stopDotScroll();
		}
	}

	protected getYPosition(x: number): number {
		const scale = 1000 / this.getWrapperWidth();
		const modifier = this.isRtl() ? 0.007 : -0.007;

		return Math.sin(modifier * scale * x) * (40 / scale) + this.getWaveWrapperHeight() / 2;
	}

	protected updateWave(): void {
		const element = this.element.querySelector(History.domSelectors.wavePath) as HTMLElement;

		let svgPath = `M0 ${this.getWaveWrapperHeight() / 2}`;

		for (let x = 0; x < this.getWrapperWidth(); x++) {
			const y = this.getYPosition(x);
			svgPath += ` L${x} ${y}`;
		}

		element.setAttribute('d', svgPath);
	}

	protected updateDots(): void {
		this.dots.forEach((dot, index): void => {
			const element = dot;
			const x =
				(element.getBoundingClientRect().width + this.dotSpacing) * index +
				this.scrollValue +
				this.getDotScrollBounds().left;
			const y = this.getYPosition(x + this.getDotDiameter() / 2) - this.getDotDiameter() / 2;

			gsap.set(element, { x });
			gsap.set(element, { y });

			if (x < this.getDotScrollBounds().left || x > this.getDotScrollBounds().right - this.getDotDiameter()) {
				element.classList.add('is-hidden');
			} else {
				element.classList.remove('is-hidden');
			}
		});
	}

	protected updateKnobY(): void {
		const draggable = this.knobDraggable[0];
		const knobDiameter = this.getKnobDiameter();
		const knobY = this.getYPosition(draggable.x + knobDiameter / 2) - knobDiameter / 2;

		gsap.set(draggable.target, { y: knobY });
	}

	protected stopDotScroll(): void {
		if (this.scrollAnimation) {
			cancelAnimationFrame(this.scrollAnimation);
			this.scrollAnimation = undefined;
		}
	}

	/**
	 * The main area of the timeline in which the slider can be moved
	 */
	protected getTimelineBounds(): { left: number; width: number; right: number } {
		const percentFromEdge = 8;
		const left = Math.round((this.getWrapperWidth() / 100) * percentFromEdge);
		const right = Math.round((this.getWrapperWidth() / 100) * (100 - percentFromEdge));

		return {
			left,
			right,
			width: right - left
		};
	}

	/**
	 * The area in which the points are visible and reacting to interaction
	 */
	protected getDotScrollBounds(): { left: number; width: number; right: number } {
		const left = Math.round(this.getTimelineBounds().left + this.getKnobDiameter() / 2 - this.getDotDiameter() / 2);
		const right = Math.round(this.getTimelineBounds().right - this.getKnobDiameter() / 2 + this.getDotDiameter() / 2);

		return {
			left,
			right,
			width: right - left
		};
	}

	/**
	 * The special Greensock object to update the range of the slider under different circumstances
	 * depending if a scroll is even necessary or dots fitting in the timeline without overflow
	 */
	protected getDraggableBounds(): RequiredBoundsMinMax {
		const maxX = (): number => {
			if (this.getDotChainWidth() > this.getDotScrollBounds().width) {
				return this.getTimelineBounds().right - this.getKnobDiameter();
			}
			// dot chain fits in range, so drag range should end with last dot
			return this.getTimelineBounds().left + this.getDotChainWidth() - this.getDotDiameter();
		};

		return {
			minX: this.getTimelineBounds().left,
			maxX: maxX(),
			minY: 0,
			maxY: 0
		};
	}

	/**
	 * The length of the dot chain as a whole, from first to last dot
	 */
	protected getDotChainWidth(): number {
		return this.dots.reduce((acc, dot, index): number => {
			const dotSpacing = index === this.dots.length - 1 ? 0 : this.dotSpacing;
			return acc + dot.getBoundingClientRect().width + dotSpacing;
		}, 0);
	}

	protected updateKnobLabelText(year: string, highlight?: boolean): void {
		const knobLabel = this.element.querySelector(History.domSelectors.knobLabel) as HTMLElement;
		knobLabel.innerText = year;

		if (highlight) {
			knobLabel.classList.add('is-highlighted');
		} else {
			knobLabel.classList.remove('is-highlighted');
		}
	}

	protected showKnobLabel(): void {
		const knobLabel = this.element.querySelector(History.domSelectors.knobLabel) as HTMLElement;
		knobLabel.classList.add('is-visible');
	}

	protected hideKnobLabel(): void {
		const knobLabel = this.element.querySelector(History.domSelectors.knobLabel) as HTMLElement;
		knobLabel.classList.remove('is-visible');
	}

	protected setActiveContent(id: string, options = { alignWithViewport: true }): void {
		const contentSelector = `${History.domSelectors.entry}[${History.attributes.entryId}=${id}]`;
		const dotSelector = `${History.domSelectors.dot}[${History.attributes.entryId}=${id}]`;
		const dot: HTMLElement | null = this.element.querySelector(dotSelector) as HTMLElement;
		const content: HTMLElement | null = this.element.querySelector(contentSelector);
		const year = dot.getAttribute(History.attributes.entryYear) as string;
		const isHighlight = dot.classList.contains('is-highlight');
		const dotIndex = this.getDotIndexWithId(id);
		const numDots = this.getDotData().length;

		const rightThreshHold = this.getDotScrollBounds().right - (this.dotSpacing + this.getDotDiameter() * 2);
		const leftThreshHold = this.getDotScrollBounds().left + (this.dotSpacing + this.getDotDiameter() * 2);

		if (!content || dotIndex === undefined || dotIndex === null) return;

		let scrollTo = null;

		const dotBounds = getRelativeBoundsTo(dot);

		if (dotBounds.left > rightThreshHold && dotIndex <= numDots) {
			const difference = dotBounds.left - this.getDotScrollBounds().right;
			scrollTo = this.scrollValue - difference - this.getDotDiameter() - this.dotSpacing * 2;

			if (Math.abs(scrollTo) > this.getScrollableAmount()) {
				scrollTo = this.getScrollableAmount() * -1;
			}
		}

		if (dotBounds.left < leftThreshHold && dotIndex >= 0) {
			const difference = this.getDotScrollBounds().left - dotBounds.left;
			scrollTo = this.scrollValue + difference + this.dotSpacing * 2;

			if (scrollTo > 0) {
				scrollTo = 0;
			}
		}

		this.showKnobLabel();

		const setContent = (): void => {
			this.hideAllContent();
			this.moveKnobToDot(id, true);
			this.currentId = id;
			content.classList.add('is-active');
			this.updateKnobLabelText(year, isHighlight);
			this.updateMobileNavigation();

			if (options.alignWithViewport) {
				this.alignModuleToTop();
			}
		};

		if (scrollTo != null && window.innerWidth > 980 && this.isScrollable()) {
			this.scrollIntoView(scrollTo, (): void => setContent());
		} else {
			setContent();
		}
	}

	/**
	 * Get current index of the selected dot HTMLElement
	 */
	protected getDotIndexWithId(id: string): number | null {
		let dotIndex = null;

		this.dots.forEach((dot, index): void => {
			const dotId = dot.getAttribute(History.attributes.entryId);
			if (dotId && dotId === id) {
				dotIndex = index;
			}
		});

		return dotIndex;
	}

	/**
	 * Gets an array of id and year from the dot HTMLElements
	 */
	protected getDotData(): { id: string; year: string }[] {
		return this.dots.map((dot): any => {
			const id = dot.getAttribute(History.attributes.entryId) as string;
			const year = dot.getAttribute(History.attributes.entryYear) as string;
			return {
				id,
				year
			};
		});
	}

	protected hideAllContent(): void {
		const entries: HTMLElement[] = Array.from(this.element.querySelectorAll(History.domSelectors.entry));
		entries.forEach((entry): void => entry.classList.remove('is-active'));
	}

	protected getWrapperWidth(): number {
		const timeline = this.element.querySelector(History.domSelectors.timeline) as HTMLElement;
		return timeline.getBoundingClientRect().width;
	}

	protected getActiveDot(): HTMLElement {
		const dotSelector = `${History.domSelectors.dot}[${History.attributes.entryId}=${this.currentId}]`;
		return this.element.querySelector(dotSelector) as HTMLElement;
	}

	protected getDotDiameter(): number {
		const dot = this.element.querySelector(History.domSelectors.dot) as HTMLElement;
		return dot.getBoundingClientRect().width;
	}

	protected getWaveWrapperHeight(): number {
		const waveWrapper = this.element.querySelector(History.domSelectors.waveWrapper) as HTMLElement;
		return waveWrapper.getBoundingClientRect().height;
	}

	protected getKnobDiameter(): number {
		const knob = this.element.querySelector(History.domSelectors.knob) as HTMLElement;
		return knob.getBoundingClientRect().width;
	}

	protected getScrollableAmount(): number {
		const difference = Math.round(this.getDotScrollBounds().width - this.getDotChainWidth());

		if (difference < 0) {
			return Math.abs(difference);
		}
		return 0;
	}

	protected isScrollable(): boolean {
		return this.getScrollableAmount() > 0;
	}

	protected getScrolledAmount(): number {
		return Math.abs(this.scrollValue);
	}

	protected updateScrollEdgeIndicators(): void {
		const firstYear = this.element.querySelector(History.domSelectors.firstYear) as HTMLElement;
		const lastYear = this.element.querySelector(History.domSelectors.lastYear) as HTMLElement;

		const firstYearX =
			this.getDotScrollBounds().left - (firstYear.getBoundingClientRect().width - this.getDotDiameter()) / 2;
		const firstYearY = this.getYPosition(firstYearX) - firstYear.getBoundingClientRect().height / 2;

		let draggableX = 0;
		if (this.getDraggableBounds() && this.getDraggableBounds().maxX) {
			draggableX = this.getDraggableBounds().maxX as number;
		}
		const lastYearX = draggableX + (this.getKnobDiameter() - lastYear.getBoundingClientRect().width) / 2;
		const lastYearY = this.getYPosition(lastYearX) - lastYear.getBoundingClientRect().height / 2;

		firstYear.style.left = `${firstYearX}px`;
		firstYear.style.top = `${firstYearY}px`;
		lastYear.style.left = `${lastYearX}px`;
		lastYear.style.top = `${lastYearY}px`;
	}

	protected startHitDetection(): void {
		this.hitDetectionInterval = window.setInterval((): void => {
			this.performHitDetection();
		}, 40);
	}

	protected stopHitDetection(): void {
		if (this.hitDetectionInterval) {
			clearInterval(this.hitDetectionInterval);
			this.hitDetectionInterval = undefined;
		}
	}

	protected performHitDetection(): void {
		const dotHitTarget = this.element.querySelector(History.domSelectors.dotHitTarget) as HTMLElement;

		this.dots.forEach((dot): void => {
			const id = dot.getAttribute(History.attributes.entryId);
			const isHighlight = dot.classList.contains('is-highlight');

			if (id && Draggable.hitTest(dot, dotHitTarget)) {
				this.currentId = id;
				const year = dot.getAttribute(History.attributes.entryYear);
				if (year) {
					this.updateKnobLabelText(year, isHighlight);
				}
			}
		});
	}

	protected moveKnobToDot(id: string, animate?: boolean): void {
		const dotSelector = `${History.domSelectors.dot}[${History.attributes.entryId}=${id}]`;
		const dot: HTMLElement | null = this.element.querySelector(dotSelector);

		if (!dot) return;

		const dotBounds = getRelativeBoundsTo(dot);
		const draggable = this.knobDraggable[0];
		const position = dotBounds.left - this.getKnobDiameter() / 2 + this.getDotDiameter() / 2;

		if (animate) {
			gsap.to(draggable.target, {
				x: position,
				duration: 0.3,
				onUpdate: (): void => {
					this.updateDraggableBounds();
					this.updateKnobY();
				},
				onComplete: (): void => {
					this.updateDraggableBounds();
					this.updateKnobY();
					this.hideKnobLabel();
				}
			});
		} else {
			gsap.set(draggable.target, {
				x: position,
				onComplete: (): void => {
					this.updateDraggableBounds();
					this.updateKnobY();
				}
			});
		}
	}

	protected scrollIntoView(scrollValue: number, callback?: () => void): void {
		gsap.to(this, {
			scrollValue,
			duration: 0.4,
			onUpdate: (): void => {
				this.updateDots();
				this.updateScrollEdgeIndicators();
			},
			onComplete: (): void => {
				if (callback) {
					callback();
				}
			}
		});
	}

	private alignModuleToTop(): void {
		// Scroll into viewport if outside
		const boundingClientRect: ClientRect = this.element.getBoundingClientRect();
		if (boundingClientRect.top <= 20 || boundingClientRect.bottom >= window.innerHeight + 20) {
			LayoutUtil.scrollElementToTopAndHideNavigation(this.element);
		}
	}

	/**
	 * Handles some edge cases with the drag handler and active dots to stay in visible area of timeline
	 * when resizing the viewport or change orientation
	 */
	protected adjustTimelinePositions(): void {
		// when active dot gets out of right bounds
		const activeDot = this.getActiveDot();
		const activeDotLeftPos = getRelativeBoundsTo(activeDot).left;
		if (activeDotLeftPos + this.getDotDiameter() > this.getDotScrollBounds().right) {
			const difference = activeDotLeftPos + this.getDotDiameter() - this.getDotScrollBounds().right;
			this.scrollValue -= difference;
		}

		// when chain is scrolled but would already fit in as a whole (screen gets wider)
		if (this.getScrolledAmount() - this.getScrollableAmount() > 0) {
			const difference = this.getScrolledAmount() - this.getScrollableAmount();
			this.scrollValue += difference;
		}
	}

	protected updateDraggableBounds(): void {
		const draggable = this.knobDraggable[0];
		draggable.applyBounds({ ...this.getDraggableBounds() });
	}

	protected isRtl(): boolean {
		return document.dir === 'rtl';
	}

	private anchorLinkHandler(event: CustomEventInit): void {
		const target = event.detail.target;
		const id = target.replace('#', '');
		const targetElement = this.element.querySelector(target);

		if (targetElement) {
			this.setActiveContent(id, { alignWithViewport: false });
		}
	}
}
